import type { ItemStockSummary, ItemStockWarehouse } from '@erp/shared';
import { ArrowUpDown, History, Plus, Warehouse as WarehouseIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge, Card, Skeleton } from '../../../components/shadcn/index.js';
import { Button, DataTable, type DataTableColumn, EmptyState } from '../../../components/ui/index.js';
import { formatDate, formatDateTime } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';

import { fmtQty } from './profileFormat.js';
import { moneyList, PackHint, QtyText, SectionTitle } from './ProfileParts.js';

interface Props {
  summary: ItemStockSummary | null;
  error: boolean;
  baseUnit: string;
  pack: { name: string; factor: number } | null;
  canCost: boolean;
  canAdjust: boolean;
  /** Open the adjust dialog, pre-set to a warehouse ('' = let the user pick). */
  onAdjust: (warehouseId: string) => void;
  /** Jump to the movements tab filtered to a warehouse. */
  onShowMovements: (warehouseId: string) => void;
}

/**
 * Stock by warehouse, in full: every branch the user may see — zeros listed,
 * negatives in red — with what it holds in pieces and in packs, what came in
 * and went out lately, when it last moved each way, and (for those who may see
 * costs) what it is costed at and worth.
 */
export function ItemBranchesTab({ summary, error, baseUnit, pack, canCost, canAdjust, onAdjust, onShowMovements }: Props): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const locale = i18n.language;

  if (error) return <Card className="p-6"><p className="text-sm text-danger">{t('detail.loadError', { defaultValue: 'Could not load.' })}</p></Card>;
  if (!summary) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;

  const days = summary.days;
  const dateOf = (iso: string | null): string => (iso ? formatDate(iso, { locale }) : '—');
  const value = (w: ItemStockWarehouse): string => (w.avgCost && w.avgCostCurrencyCode
    ? formatMoney(String(Number(w.quantity) * Number(w.avgCost)), { locale, currency: w.avgCostCurrencyCode })
    : '—');

  const columns: DataTableColumn<ItemStockWarehouse>[] = [
    {
      id: 'warehouse',
      header: t('detail.col.warehouse', { defaultValue: 'Warehouse' }),
      sortValue: (w) => w.name,
      cell: (w) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{w.name}</span>
          <span className="font-mono text-[11px] text-fg-subtle">{w.code}</span>
          {!w.isActive && <Badge variant="warning">{t('profile.stock.inactiveWarehouse', { defaultValue: 'Inactive warehouse' })}</Badge>}
          {Number(w.quantity) < 0 && <Badge variant="danger">{t('profile.stock.negative', { defaultValue: 'Below zero' })}</Badge>}
        </span>
      ),
    },
    {
      id: 'onHand',
      header: t('profile.stock.onHand', { defaultValue: 'On hand' }),
      align: 'end',
      sortValue: (w) => Number(w.quantity),
      cell: (w) => <QtyText value={w.quantity} unit={baseUnit} strong />,
    },
  ];
  if (pack) {
    columns.push({
      id: 'packs',
      header: t('profile.stock.inPacks', { defaultValue: 'In {{pack}}', pack: pack.name }),
      align: 'end',
      sortValue: (w) => Number(w.quantity),
      cell: (w) => <PackHint quantity={w.quantity} pack={pack} baseUnit={baseUnit} />,
    });
  }
  columns.push(
    {
      id: 'in',
      header: t('profile.stock.inDays', { defaultValue: 'In · {{days}}d', days }),
      align: 'end',
      sortValue: (w) => Number(w.periodIn),
      cellClassName: 'font-mono tabular-nums',
      cell: (w) => (Number(w.periodIn) > 0 ? <span dir="ltr" className="text-success">+{fmtQty(w.periodIn, locale)}</span> : <span className="text-fg-subtle">—</span>),
    },
    {
      id: 'out',
      header: t('profile.stock.outDays', { defaultValue: 'Out · {{days}}d', days }),
      align: 'end',
      sortValue: (w) => Number(w.periodOut),
      cellClassName: 'font-mono tabular-nums',
      cell: (w) => (Number(w.periodOut) > 0 ? <span dir="ltr" className="text-danger">−{fmtQty(w.periodOut, locale)}</span> : <span className="text-fg-subtle">—</span>),
    },
    {
      id: 'lastIn',
      header: t('profile.stock.lastIn', { defaultValue: 'Last in' }),
      sortValue: (w) => w.lastInAt ?? '',
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (w) => dateOf(w.lastInAt),
    },
    {
      id: 'lastOut',
      header: t('profile.stock.lastOut', { defaultValue: 'Last out' }),
      sortValue: (w) => w.lastOutAt ?? '',
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (w) => dateOf(w.lastOutAt),
    },
    {
      id: 'lastMove',
      header: t('profile.stock.lastMovement', { defaultValue: 'Last movement' }),
      sortValue: (w) => w.lastMovementAt ?? '',
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (w) => (w.lastMovementAt ? formatDateTime(w.lastMovementAt, { locale }) : '—'),
    },
  );
  if (canCost) {
    columns.push(
      {
        id: 'avgCost',
        header: t('profile.stock.avgCost', { defaultValue: 'Average cost' }),
        align: 'end',
        sortValue: (w) => (w.avgCost ? Number(w.avgCost) : null),
        cellClassName: 'whitespace-nowrap font-mono tabular-nums',
        cell: (w) => (w.avgCost && w.avgCostCurrencyCode ? formatMoney(w.avgCost, { locale, currency: w.avgCostCurrencyCode }) : '—'),
      },
      {
        id: 'value',
        header: t('profile.stock.value', { defaultValue: 'Value at cost' }),
        align: 'end',
        sortValue: (w) => (w.avgCost ? Number(w.quantity) * Number(w.avgCost) : null),
        cellClassName: 'whitespace-nowrap font-mono tabular-nums font-semibold',
        cell: value,
      },
    );
  }
  columns.push({
    id: 'actions',
    header: '',
    align: 'end',
    cell: (w) => (
      <span className="inline-flex items-center gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={() => onShowMovements(w.warehouseId)}
          title={t('profile.stock.showMovements', { defaultValue: 'Movements in this branch' })}
          aria-label={t('profile.stock.showMovements', { defaultValue: 'Movements in this branch' })}>
          <History className="h-4 w-4" aria-hidden />
        </Button>
        {canAdjust && w.isActive && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onAdjust(w.warehouseId)}
            title={t('profile.actions.adjust', { defaultValue: 'Adjust stock' })}
            aria-label={`${t('profile.actions.adjust', { defaultValue: 'Adjust stock' })} — ${w.name}`}>
            <ArrowUpDown className="h-4 w-4" aria-hidden />
          </Button>
        )}
      </span>
    ),
  });

  const totals = summary.totals;
  return (
    <Card className="overflow-hidden p-0">
      <div className="px-4 pt-4">
        <SectionTitle
          icon={WarehouseIcon}
          end={canAdjust ? (
            <Button size="sm" onClick={() => onAdjust('')}>
              <Plus className="h-4 w-4" aria-hidden />{t('profile.actions.adjust', { defaultValue: 'Adjust stock' })}
            </Button>
          ) : undefined}
        >
          {t('detail.stockByWarehouse', { defaultValue: 'Stock by warehouse' })}
        </SectionTitle>
      </div>
      {summary.warehouses.length === 0 ? (
        <div className="p-4"><EmptyState icon={<WarehouseIcon className="h-7 w-7" aria-hidden />} title={t('profile.stock.noWarehouses', { defaultValue: 'No warehouse you can see holds this product.' })} /></div>
      ) : (
        <DataTable
          columns={columns}
          data={summary.warehouses}
          rowKey={(w) => w.warehouseId}
          searchable={false}
          paginated={false}
          rowClassName={(w) => (Number(w.quantity) < 0 ? 'bg-bg-subtle' : undefined)}
          footerRows={[{
            key: 'total',
            className: 'font-semibold',
            cells: {
              warehouse: t('profile.stock.total', { defaultValue: 'Total' }),
              onHand: <QtyText value={totals.quantity} unit={baseUnit} strong />,
              ...(pack ? { packs: <PackHint quantity={totals.quantity} pack={pack} baseUnit={baseUnit} /> } : {}),
              in: <span dir="ltr" className="font-mono tabular-nums text-success">+{fmtQty(totals.periodIn, locale)}</span>,
              out: <span dir="ltr" className="font-mono tabular-nums text-danger">−{fmtQty(totals.periodOut, locale)}</span>,
              lastMove: totals.lastMovementAt ? formatDateTime(totals.lastMovementAt, { locale }) : '—',
              ...(canCost ? { value: moneyList(summary.values, locale) } : {}),
            },
          }]}
        />
      )}
    </Card>
  );
}
